var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { ComposerMode, } from '../../types/index';
import { InviteesSelector } from './InviteeSelector';
import { useAddSpaceFromSearch } from '../../hooks/useAddSpaceFromSearch';
import { useInviteeAvailability } from '../../hooks/useInviteeAvailability';
import { useInviteesDisabledSetting } from '../../hooks/useInviteesDisabledSetting';
import { useEventComposerGlobalContext } from '../../composers/EventComposerGlobalContext';
import { useConfig } from '../../context/ConfigContext';
var sameInvitee = function (a, b) {
    return ((a.user_id && b.user_id === a.user_id) || (a.email && b.email === a.email));
};
var isCurrentUserInvitee = function (invitee, currentUserId, currentUserEmail) {
    var _a;
    if (currentUserId != null && invitee.user_id === currentUserId) {
        return true;
    }
    if (currentUserEmail &&
        ((_a = invitee.email) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === currentUserEmail.toLowerCase()) {
        return true;
    }
    return false;
};
var InviteesWrapper = function (_a) {
    var _b, _c, _d;
    var hideInvitees = _a.hideInvitees;
    var setValue = useFormContext().setValue;
    var addSpaceById = useAddSpaceFromSearch().addSpaceById;
    var userContext = useConfig().userContext;
    var _e = __read(useWatch({
        name: ['invitees', 'attendeeCount', 'spaces'],
    }), 3), invitees = _e[0], attendeeCount = _e[1], spaces = _e[2];
    var addInvitee = function (invitee) {
        var hasInvitee = invitees.find(function (i) {
            return sameInvitee(i, invitee);
        });
        if (!hasInvitee) {
            setValue('invitees', __spreadArray(__spreadArray([], __read(invitees), false), [invitee], false), {
                shouldDirty: true,
            });
        }
    };
    var removeInvitee = function (invitee) {
        setValue('invitees', invitees.filter(function (i) { return !sameInvitee(i, invitee); }), {
            shouldDirty: true,
        });
    };
    var setAttendeeCount = function (value) {
        setValue('attendeeCount', value, {
            shouldDirty: true,
        });
    };
    var _f = useInviteeAvailability({
        hideInvitees: hideInvitees,
    }), inviteesWithAvailability = _f.inviteesWithAvailability, isLoading = _f.isLoading;
    // EVENT_CREATION_INVITEES_DISABLED org setting. Only applied in CREATE mode
    // (mirrors Angular blocking new-event invitee selection); EDIT is unaffected
    // so existing events can still be managed.
    var composerMode = useEventComposerGlobalContext().composerMode;
    var inviteesDisabled = useInviteesDisabledSetting().inviteesDisabled;
    var disableInviteeSearch = inviteesDisabled && composerMode === ComposerMode.CREATE;
    // CREATE: flag the drafting user as the event's creator. The organizer is
    // decided at save time by the calendar backend (the space resource calendar
    // often ends up organizer even when a personal calendar is selected), so we
    // never predict it here — the `organizer` flag only comes from the API when
    // the saved event is reopened.
    useEffect(function () {
        var _a, _b, _c;
        if (composerMode !== ComposerMode.CREATE || !(invitees === null || invitees === void 0 ? void 0 : invitees.length)) {
            return;
        }
        var currentUserId = ((_a = userContext.currentUser) === null || _a === void 0 ? void 0 : _a.id)
            ? parseInt(userContext.currentUser.id, 10)
            : null;
        var currentUserEmail = (_c = (_b = userContext.currentUser) === null || _b === void 0 ? void 0 : _b.primaryEmail) === null || _c === void 0 ? void 0 : _c.email;
        if (currentUserId == null && !currentUserEmail) {
            return;
        }
        var changed = false;
        var nextInvitees = invitees.map(function (invitee) {
            if (!isCurrentUserInvitee(invitee, currentUserId, currentUserEmail) ||
                invitee.creator) {
                return invitee;
            }
            changed = true;
            return __assign(__assign({}, invitee), { creator: true });
        });
        if (changed) {
            setValue('invitees', nextInvitees, { shouldDirty: false });
        }
    }, [
        composerMode,
        invitees,
        setValue,
        (_b = userContext.currentUser) === null || _b === void 0 ? void 0 : _b.id,
        (_d = (_c = userContext.currentUser) === null || _c === void 0 ? void 0 : _c.primaryEmail) === null || _d === void 0 ? void 0 : _d.email,
    ]);
    return (_jsx(InviteesSelector, { setAttendeeCount: setAttendeeCount, attendeeCount: attendeeCount, hideInvitees: hideInvitees, disableInviteeSearch: disableInviteeSearch, invitees: invitees, inviteesWithAvailability: inviteesWithAvailability, isLoadingAvailability: isLoading, spaces: spaces, addInvitee: addInvitee, removeInvitee: removeInvitee, onSpaceSelected: addSpaceById }));
};
export default InviteesWrapper;
